// @ts-nocheck
/**
 * PDF Report Generator for Educational ROI Calculators (Epic 18)
 * Generates professional PDF reports from calculator results
 * Uses client-side PDF generation for immediate downloads
 */

import { jsPDF } from 'jspdf';
import { ROICalculationResults, TimeSavingsCalculationResults } from '../../shared/interfaces/EducationalCalculators';

interface ReportData {
  calculatorType: string;
  results: ROICalculationResults | TimeSavingsCalculationResults | any;
  inputs: Record<string, any>;
  timestamp: Date;
  userInfo?: {
    name?: string;
    institution?: string;
    role?: string;
  };
}

export class PDFReportGenerator {
  private doc: jsPDF;
  private readonly pageWidth = 210; // A4 width in mm
  private readonly pageHeight = 297; // A4 height in mm
  private readonly margin = 20;
  private yPosition = 30;

  constructor() {
    this.doc = new jsPDF();
  }

  /**
   * Generate a comprehensive ROI report
   */
  async generateROIReport(reportData: ReportData): Promise<Blob> {
    this.resetDocument();

    // Header
    this.addHeader(reportData);

    // Executive Summary
    this.addExecutiveSummary(reportData);

    // Key Metrics
    this.addKeyMetrics(reportData);

    // Detailed Analysis
    this.addDetailedAnalysis(reportData);

    // Methodology
    this.addMethodology(reportData);

    // Footer
    this.addFooter();

    return this.doc.output('blob');
  }

  private resetDocument(): void {
    this.doc = new jsPDF();
    this.yPosition = 30;
  }

  private addHeader(reportData: ReportData): void {
    // Logo and branding (placeholder for actual logo)
    this.doc.setFillColor(138, 63, 252); // Galaxy Purple
    this.doc.rect(this.margin, 10, this.pageWidth - 2 * this.margin, 15, 'F');

    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(20);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Educational ROI Analysis Report', this.margin + 5, 20);

    // Reset colors
    this.doc.setTextColor(0, 0, 0);
    this.yPosition = 35;

    // Report metadata
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`Generated: ${reportData.timestamp.toLocaleDateString()}`, this.margin, this.yPosition);
    this.yPosition += 7;

    if (reportData.userInfo?.name) {
      this.doc.text(`Prepared for: ${reportData.userInfo.name}`, this.margin, this.yPosition);
      this.yPosition += 7;
    }

    if (reportData.userInfo?.institution) {
      this.doc.text(`Institution: ${reportData.userInfo.institution}`, this.margin, this.yPosition);
      this.yPosition += 7;
    }

    this.doc.text(
      `Calculator Type: ${this.formatCalculatorType(reportData.calculatorType)}`,
      this.margin,
      this.yPosition
    );
    this.yPosition += 15;
  }

  private addExecutiveSummary(reportData: ReportData): void {
    this.addSectionHeader('Executive Summary');

    const results = reportData.results;
    let summaryText = '';

    if (this.isROIResults(results)) {
      summaryText =
        `This analysis demonstrates significant potential returns from educational technology investment. ` +
        `With an estimated annual savings of $${results.annualSavings?.toLocaleString() || 'N/A'} and a ` +
        `three-year ROI of ${results.threeYearROI || 'N/A'}%, the investment shows strong financial benefits. ` +
        `The payback period is projected at ${results.paybackPeriod || 'N/A'} months, with substantial ` +
        `improvements in student outcomes and operational efficiency.`;
    } else if (this.isTimeSavingsResults(results)) {
      summaryText =
        `This time savings analysis reveals significant productivity opportunities. ` +
        `Teachers can save approximately ${results.weeklyTimeSaved || 'N/A'} hours per week, ` +
        `translating to $${results.monetaryValue?.toLocaleString() || 'N/A'} in annual value. ` +
        `This represents a ${results.productivityIncrease || 'N/A'}% increase in productivity ` +
        `and the capacity to serve ${results.additionalStudentsServed || 'N/A'} additional students.`;
    }

    this.addWrappedText(summaryText, this.margin, this.yPosition, this.pageWidth - 2 * this.margin);
    this.yPosition += 20;
  }

  private addKeyMetrics(reportData: ReportData): void {
    this.addSectionHeader('Key Performance Indicators');

    const results = reportData.results;
    const metricsStartY = this.yPosition;

    if (this.isROIResults(results)) {
      this.addMetricBox('3-Year ROI', `${results.threeYearROI || 0}%`, this.margin, this.yPosition, 40, 25);
      this.addMetricBox(
        'Annual Savings',
        `$${results.annualSavings?.toLocaleString() || '0'}`,
        this.margin + 45,
        this.yPosition,
        40,
        25
      );
      this.addMetricBox(
        'Payback Period',
        `${results.paybackPeriod || 0} months`,
        this.margin + 90,
        this.yPosition,
        40,
        25
      );
      this.addMetricBox('Cost/Student', `$${results.costPerStudent || 0}`, this.margin + 135, this.yPosition, 40, 25);
    } else if (this.isTimeSavingsResults(results)) {
      this.addMetricBox('Weekly Savings', `${results.weeklyTimeSaved || 0} hours`, this.margin, this.yPosition, 40, 25);
      this.addMetricBox(
        'Annual Value',
        `$${results.monetaryValue?.toLocaleString() || '0'}`,
        this.margin + 45,
        this.yPosition,
        40,
        25
      );
      this.addMetricBox(
        'Productivity Gain',
        `${results.productivityIncrease || 0}%`,
        this.margin + 90,
        this.yPosition,
        40,
        25
      );
      this.addMetricBox(
        'Additional Students',
        `${results.additionalStudentsServed || 0}`,
        this.margin + 135,
        this.yPosition,
        40,
        25
      );
    }

    this.yPosition = metricsStartY + 35;
  }

  private addDetailedAnalysis(reportData: ReportData): void {
    this.addSectionHeader('Detailed Financial Analysis');

    const results = reportData.results;

    if (this.isROIResults(results) && results.breakdownByCategory) {
      this.doc.setFontSize(11);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Cost-Benefit Breakdown:', this.margin, this.yPosition);
      this.yPosition += 10;

      this.doc.setFont('helvetica', 'normal');
      Object.entries(results.breakdownByCategory).forEach(([category, value]) => {
        this.doc.text(`• ${category}: $${value.toLocaleString()}`, this.margin + 5, this.yPosition);
        this.yPosition += 7;
      });
    }

    this.yPosition += 10;

    // Input Parameters
    this.addSectionHeader('Analysis Parameters', 14);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');

    Object.entries(reportData.inputs).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        this.doc.text(`${formattedKey}: ${value}`, this.margin + 5, this.yPosition);
        this.yPosition += 6;
      }
    });

    this.yPosition += 15;
  }

  private addMethodology(reportData: ReportData): void {
    this.addSectionHeader('Methodology & Assumptions');

    const methodologyText =
      `This analysis is based on industry benchmarks and research-backed assumptions about ` +
      `educational technology implementation. ROI calculations consider direct cost savings, productivity gains, ` +
      `and improved student outcomes. Time savings estimates are based on typical educator workflows and ` +
      `automation capabilities. Results may vary based on specific implementation factors, adoption rates, ` +
      `and institutional characteristics.`;

    this.addWrappedText(methodologyText, this.margin, this.yPosition, this.pageWidth - 2 * this.margin);
    this.yPosition += 20;

    // Disclaimer
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'italic');
    const disclaimerText =
      `Disclaimer: This analysis is for planning purposes only. Actual results may vary based on ` +
      `implementation specifics, user adoption, and other factors. Please consult with implementation specialists ` +
      `for detailed planning and customized analysis.`;

    this.addWrappedText(disclaimerText, this.margin, this.yPosition, this.pageWidth - 2 * this.margin);
  }

  private addFooter(): void {
    const footerY = this.pageHeight - 20;
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(100, 100, 100);

    this.doc.text('Generated by Sigma08 Educational ROI Calculator', this.margin, footerY);
    this.doc.text(`Page 1 of 1`, this.pageWidth - this.margin - 20, footerY);

    // Contact information
    this.doc.text(
      'For questions or detailed consultation, contact our education specialists.',
      this.margin,
      footerY + 7
    );
  }

  private addSectionHeader(title: string, fontSize: number = 16): void {
    this.doc.setFontSize(fontSize);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(138, 63, 252); // Galaxy Purple
    this.doc.text(title, this.margin, this.yPosition);

    // Underline
    const textWidth = this.doc.getTextWidth(title);
    this.doc.line(this.margin, this.yPosition + 2, this.margin + textWidth, this.yPosition + 2);

    this.doc.setTextColor(0, 0, 0);
    this.yPosition += 15;
  }

  private addMetricBox(label: string, value: string, x: number, y: number, width: number, height: number): void {
    // Box border
    this.doc.setDrawColor(138, 63, 252);
    this.doc.setLineWidth(0.5);
    this.doc.rect(x, y, width, height);

    // Value
    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(138, 63, 252);
    const valueWidth = this.doc.getTextWidth(value);
    this.doc.text(value, x + (width - valueWidth) / 2, y + 12);

    // Label
    this.doc.setFontSize(8);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(0, 0, 0);
    const labelWidth = this.doc.getTextWidth(label);
    this.doc.text(label, x + (width - labelWidth) / 2, y + 20);
  }

  private addWrappedText(text: string, x: number, y: number, maxWidth: number): void {
    const lines = this.doc.splitTextToSize(text, maxWidth);
    this.doc.setFontSize(11);
    this.doc.setFont('helvetica', 'normal');

    lines.forEach((line: string) => {
      this.doc.text(line, x, y);
      y += 7;
    });

    this.yPosition = y + 5;
  }

  private formatCalculatorType(type: string): string {
    return type
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private isROIResults(results: any): results is ROICalculationResults {
    return results && typeof results.threeYearROI !== 'undefined';
  }

  private isTimeSavingsResults(results: any): results is TimeSavingsCalculationResults {
    return results && typeof results.weeklyTimeSaved !== 'undefined';
  }
}

/**
 * Utility function to generate and download PDF report
 */
export async function generateAndDownloadReport(reportData: ReportData): Promise<void> {
  const generator = new PDFReportGenerator();
  const pdfBlob = await generator.generateROIReport(reportData);

  // Create download link
  const url = URL.createObjectURL(pdfBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ROI-Analysis-${reportData.calculatorType}-${new Date().getTime()}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
